/**
 * Resumable uploads to the server's TUS endpoint (design §6.1.4). An
 * interrupted upload resumes, including after a page reload once the file is
 * chosen again, until the server expires it (24 h).
 *
 * @module
 */
import * as tus from "tus-js-client";
import { getCsrfToken } from "../api/client";

export type Outcome = "catalogued" | "needs_review" | "duplicate" | "rejected";

export interface UploadCallbacks {
  onProgress(sent: number, total: number): void;
  onDone(outcome: Outcome | null): void;
  onError(message: string): void;
}

/**
 * `folder`: the folder it was picked from, naming the unit. `resumeUrl`: an
 * unfinished upload of this file the server already holds. Not started:
 * see [`begin`].
 */
export function createUpload(
  file: File,
  folder: string | undefined,
  batchId: string,
  cb: UploadCallbacks,
  resumeUrl?: string,
  sha256?: string,
): tus.Upload {
  return new tus.Upload(file, {
    endpoint: "/api/uploads",
    uploadUrl: resumeUrl ?? null,
    chunkSize: 16 * 1024 * 1024,
    // About 12 minutes of retries, so a dropped network or a server restart is ridden out.
    retryDelays: [
      0,
      1000,
      3000,
      5000,
      10000,
      20000,
      30000,
      ...Array<number>(10).fill(60000),
    ],
    // `sha256`: the whole file as first read; the server rejects it if what arrives differs.
    metadata: {
      batch: batchId,
      filename: file.name,
      ...(folder ? { folder } : {}),
      ...(sha256 ? { sha256 } : {}),
    },
    storeFingerprintForResuming: true,
    removeFingerprintOnSuccess: true,
    onBeforeRequest(req) {
      const token = getCsrfToken();
      if (token) req.setHeader("X-CSRF-Token", token);
    },
    onProgress: cb.onProgress,
    onSuccess({ lastResponse }) {
      cb.onDone(
        (lastResponse.getHeader("Avgv-Import-Outcome") as
          | Outcome
          | undefined) ?? null,
      );
    },
    onError(err) {
      const body =
        err instanceof tus.DetailedError
          ? err.originalResponse?.getBody()
          : undefined;
      cb.onError(errorMessage(body) ?? err.message);
    },
  });
}

/** Resumes an earlier attempt from this browser, if any, then starts, unless `cancelled()` by then. */
export async function begin(
  upload: tus.Upload,
  batchId: string,
  cancelled: () => boolean,
): Promise<void> {
  if (!upload.options.uploadUrl) {
    const previous = await upload.findPreviousUploads();
    const sameBatch = previous.find((p) => p.metadata.batch === batchId);
    if (sameBatch) upload.resumeFromPreviousUpload(sameBatch);
  }
  if (!cancelled()) upload.start();
}

function errorMessage(body: string | undefined): string | undefined {
  if (!body) return undefined;
  try {
    const parsed: unknown = JSON.parse(body);
    if (
      parsed &&
      typeof parsed === "object" &&
      "message" in parsed &&
      typeof parsed.message === "string"
    ) {
      return parsed.message;
    }
  } catch {
    // Not JSON: fall back to the client's own message.
  }
  return undefined;
}

/** Abandons an unfinished upload; the server deletes what it received. */
export async function cancelUpload(id: string): Promise<void> {
  const headers: Record<string, string> = { "Tus-Resumable": "1.0.0" };
  const token = getCsrfToken();
  if (token) headers["X-CSRF-Token"] = token;
  const res = await fetch(`/api/uploads/${id}`, {
    method: "DELETE",
    headers,
    credentials: "same-origin",
  });
  if (!res.ok && res.status !== 404)
    throw new Error(`Cancelling failed (${res.status})`);
}
